<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <MessageContainer
        v-if="isLdapPolicy"
        :class="$style.messageContainer"
        level="info"
      >
        <span>
          Conditional Access Policies are only evaluated for LDAP applications that
          are targeted to the following URL: <strong>{{ getLdapUrl() }}</strong>
        </span>

        <div
          v-if="isItemBeingCreated"
          :class="$style.ldapLabelWrapper"
        >
          <input
            id="ldapHostnameCheckbox"
            v-validate="'required'"
            data-test-id="ldapHostnameCheckbox"
            name="ldapHostname"
            type="checkbox"
            :value="isLdapPolicyChecked"
            @change="toggleLdapPolicy"
          >
          <label
            :class="$style.ldapLabelText"
            for="ldapHostnameCheckbox"
          >
            I have updated the LDAP hostname for applications I want affected by this policy
          </label>
        </div>

        <span :class="$style.error">
          <transition :name="$style.fade">
            <span v-if="$validator.errors.any()">
              {{ getMessageForError($validator.errors, 'ldapHostname') }}
            </span>
          </transition>
        </span>
      </MessageContainer>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          General Info
        </h2>
        <GuideMessage
          caretPosition="left"
          :class="$style.generalGuide"
          name="capGeneralInfo"
        />
        <GeneralInfoSectionv2
          :value="item || {}"
          @input="handleValueChange"
        />
      </div>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Assignments
        </h2>
        <AssignmentsSectionLegacy
          :previousValue="previousItem"
          :value="item"
          :valueChange="handleValueChange"
        />
      </div>

      <div
        v-if="shouldShowConditionsSection"
        :class="$style.sectionRoot"
      >
        <h2 :class="$style.sectionHeader">
          <span>Conditions (Optional)</span>
          <PremiumFeatureBadge
            v-if="showIcon"
            :class="$style.badge"
            customText="PREMIUM"
            :data-test-id="$testId('premiumBadge')"
            :showLearnMoreLink="false"
            trialStart=""
          />
        </h2>

        <ConditionsSection
          :value="item"
          @input="handleValueChange"
        />
      </div>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Action
        </h2>
        <ActionSection
          :value="item || {}"
          @input="handleValueChange"
        />
      </div>
    </template>
  </DetailsAsideLayout>
</template>

<script>
import { cloneDeep, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { authnPolicyType } from 'client/Security/AuthNPolicyType';
import { getLdapUrl } from 'client/Security/utils/AuthnPolicyUtilities';
import { ldapGuides, ssoGuidesLegacy, userPortalGuidesLegacy } from 'client/store/modules/guidedSetup/conditionalAccessConfig';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import ActionSection from 'client/Security/AuthNPoliciesAsidePanel/ActionSection';
import AssignmentsSectionLegacy from 'client/Security/AuthNPoliciesAsidePanel/AssignmentsSectionLegacy.vue';
import ConditionsSection from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSectionLegacy/ConditionsSectionLegacy.vue';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import GeneralInfoSectionv2 from 'client/Security/AuthNPoliciesAsidePanel/GeneralInfoSectionv2.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';
import PremiumFeatureBadge from 'client/components/Badges/PremiumFeatureBadge.vue';
import platformPlusFeatureConstants from 'client/util/Constants/PlatformPlusFeatureConstants';

import { getMessageForError } from 'client/components/FormValidation/helpers/validationHelpers';

const authNPolicyModel = StoreModelMap[ItemTypeConsts.authNPolicies];
const platformPlusFeatureList = [
  platformPlusFeatureConstants.deviceTrust,
  platformPlusFeatureConstants.networkTrust,
];

if (authNPolicyModel == null) {
  throw new Error('Vuex module "authNPolicyModel" is not defined');
}

export default {
  name: 'AuthNPoliciesAsidePanelSectionsLegacy',

  components: {
    ActionSection,
    AssignmentsSectionLegacy,
    ConditionsSection,
    DetailsAsideLayout,
    GeneralInfoSectionv2,
    GuideMessage,
    MessageContainer,
    PremiumFeatureBadge,
  },

  data() {
    return {
      isLdapPolicyChecked: false,
    };
  },

  computed: {
    ...mapGetters('FeatureTrialModule', [
      'getPremiumBadgeProps',
    ]),

    ...mapGetters('PlanModule', [
      'planHasFeature',
    ]),

    ...mapGetters(authNPolicyModel, [
      'item',
      'previousItem',
      'isItemBeingCreated',
    ]),

    isLdapPolicy() {
      return this.item.type === authnPolicyType.ldap;
    },

    shouldShowConditionsSection() {
      return !this.isLdapPolicy;
    },

    showIcon() {
      return this.getPremiumBadgeProps(
        trialFeatures.zeroTrust.featureCode,
      ).nonHeaderBadgeVisibility;
    },
  },

  inject: ['$validator'],

  platformPlusFeatureList,

  beforeMount() {
    const vValidateDict = {
      custom: {
        ldapHostname: {
          required: 'Required',
        },
      },
    };

    this.$validator.localize('en', vValidateDict);

    const { guide: guideName, type } = this.$route.query;
    const guideConfig = get({
      application: ssoGuidesLegacy,
      userportal: userPortalGuidesLegacy,
      ldap: ldapGuides,
    }, type);
    const guide = get(guideConfig, guideName);

    if (guide) {
      this.initializeGuide({ type, guide });
    } else {
      this.dismissGuide();
    }
  },

  methods: {
    ...mapActions(authNPolicyModel, ['updateItem']),
    ...mapActions('GuidedSetupModule', ['initializeGuide', 'dismissGuide']),
    getLdapUrl,
    getMessageForError,

    handleValueChange(newValue) {
      this.updateItem(cloneDeep(newValue));
    },

    toggleLdapPolicy() {
      this.isLdapPolicyChecked = !this.isLdapPolicyChecked;
    },
  },
};
</script>

<style module>
  @import '../../css/sections.css';
  @import "../../components/FormValidation/css/formFields.css";

  .messageContainer {
    font-size: var(--jcBody);
  }

  .generalGuide div {
    right: 40px;
    top: -10px;
  }

  .badge {
    margin-left: var(--jc-spacer-small);
  }

  .sectionHeader.sectionHeader.sectionHeader {
    align-items: center;
    display: flex;
    margin-bottom: 1em;
  }

  .ldapLabelWrapper {
    margin-top: 1em;
  }

  .ldapLabelText {
    margin-left: 0.5em;
  }

  .error {
    color: var(--jcError900);
    flex-basis: 100%;
    margin-left: 1.5em;
  }
</style>
